import { DEFAULT_CORNER_RADIUS, EACH } from '@common/constants';
import {
    ApplianceModel,
    CornerRadiusModel,
    DrawingAppliance,
    DrawingShape,
    EdgeModel,
    PlumbingProduct,
    SelectedItemMap,
    SelectedItemProperties,
} from '@common/models';
import { OptionalItem } from '@common/models/optional-items-response';
import { getProductDescription } from '@common/utils';
import { CommonProductQueryParams } from '@ee-api/queryParams';

import {
    ApplianceChecker,
    BuilderCommonArgs,
    CheckerContext,
    CornerChecker,
    EdgeChecker,
    EquivalentProductChecker,
    SelectedItemChecker,
} from '../checkers/equivalentProductMatchingCheckers';
import {
    AlertWithMetadata,
    CornerInfoWithProductInfo,
    EstimateItem,
    ProductMatchingSummary,
    ProductMatchingTargetParameters,
    SelectedItemWithFullRecord,
} from '../interfaces/ProductMatchingSummary';

/** Higher order function that returns a function to find an item in a catalog that match by product id */
export const createIsInCatalogFinder = (productId: number) => (catalogItem: { productId: number }) =>
    catalogItem.productId === productId;

/** Higher order function that returns a function to find an equvalent item in a catalog that match by masterSku */
export const createEquivalentFinder = (masterSku: string) => (catalogItem: { masterSku: string }) =>
    !!masterSku && catalogItem.masterSku === masterSku;
/**
 * adds a selectedItemWithFullRecord to an existing selectedItemMap
 * @returns {SelectedItemMap}
 */
export const addSelectedItemWithFullRecordToSelectedItemMap = (
    selectedItemMap: SelectedItemMap,
    selectedItemWithFullRecord: SelectedItemWithFullRecord
): SelectedItemMap => {
    return selectedItemMap.set(
        selectedItemWithFullRecord.selectedItem.productId,
        selectedItemWithFullRecord.selectedItemProperties
    );
};

export const createDrawingShapesToSummaryReducerUsingCornersChecker = (
    newCatalog: CornerRadiusModel[],
    currentCatalog: CornerRadiusModel[]
) => {
    return (
        acc: ProductMatchingSummary<CornerInfoWithProductInfo, DrawingShape>,
        next: DrawingShape,
        index: number
    ): ProductMatchingSummary<CornerInfoWithProductInfo, DrawingShape> => {
        const cornerMap = next.cornerMap;

        if (!Object.keys(cornerMap).length) {
            return acc;
        }

        const ctx: CheckerContext<CornerRadiusModel> = {
            newCatalog,
        };

        return Object.entries(cornerMap)
            .map<CornerInfoWithProductInfo | undefined>(([key, cornerInfo]) => {
                const matchingCorner = currentCatalog.find((corner) => corner.productId === cornerInfo.productId);
                return matchingCorner
                    ? {
                          key,
                          cornerInfo,
                          masterSku: matchingCorner?.masterSku,
                          productName: getProductDescription(matchingCorner),
                          imageUrl: matchingCorner.imageUrl,
                      }
                    : undefined;
            })
            .filter(
                (cornerFullRecordOrUndefined): cornerFullRecordOrUndefined is CornerInfoWithProductInfo =>
                    !!cornerFullRecordOrUndefined
            )
            .filter((cornerFullRecord) => cornerFullRecord.cornerInfo.productId) //check if is product
            .reduce((_acc, cornerFullRecord) => {
                return addToSummary({
                    summary: _acc,
                    item: cornerFullRecord,
                    index,
                    checker: CornerChecker,
                    ctx,
                });
            }, acc);
    };
};

export const productInfoFromItem = (
    item: EstimateItem
): { productName: string; productId: number; quantity: number; unitOfMeasure: string; imageUrl?: string } => {
    if ('productId' in item) {
        return {
            productName: getProductDescription(item),
            productId: item.productId,
            unitOfMeasure: item.unitOfMeasure ?? EACH,
            quantity: 1,
            imageUrl: item.imageUrl,
        };
    } else if ('cornerInfo' in item) {
        return {
            productName: item.productName,
            productId: item.cornerInfo.productId ?? 0,
            unitOfMeasure: EACH,
            quantity: 1,
            imageUrl: item.imageUrl,
        };
    } else if ('applianceModel' in item) {
        return {
            productName: getProductDescription(item.applianceModel),
            productId: item.applianceModel.productId,
            unitOfMeasure: item.applianceModel.unitOfMeasure ?? EACH,
            quantity: 1,
            imageUrl: item.applianceModel.beautyShotImageUrl ?? item.applianceModel.imageUrl ?? undefined,
        };
    } else {
        return {
            productName: getProductDescription(item.selectedItem),
            productId: item.selectedItem.productId,
            unitOfMeasure: item.selectedItem.unitOfMeasure ?? EACH,
            quantity: item.selectedItemProperties.quantity,
            imageUrl: item.selectedItem.imageUrl ?? undefined,
        };
    }
};

export const isCommonProductQueryParams = (
    args: ProductMatchingTargetParameters
): args is Required<CommonProductQueryParams>['params'] => {
    return 'prodQteGrpID' in args;
};

const emptyArray = [] satisfies DrawingShape[] | SelectedItemWithFullRecord[];

/**
 * is the given item a radius corner?
 * @param item
 */
const isARadiusCorner = (item: EstimateItem): item is CornerInfoWithProductInfo => {
    return 'cornerInfo' in item && !item.cornerInfo.diagonalLength;
};

export const summaryToAlertsWithMetadataReducer = (
    acc: AlertWithMetadata[],
    next: ProductMatchingSummary<EstimateItem, unknown>
): AlertWithMetadata[] => {
    /**The removed array of the summary without duplicate products */
    const removedWithoutDuplicates = Object.values(
        next.removed.reduce((acc, curr) => {
            const { productId } = productInfoFromItem(curr);
            if (!acc[productId]) {
                acc[productId] = curr;
            }
            return acc;
        }, {} as Record<string, EstimateItem>)
    );

    /**
     * filter out any radius corners that are default value - they could only change back to a default value anyhow, so no alert needed
     */
    const removedWithoutDuplicatesWithoutCorners = removedWithoutDuplicates.filter((item) => {
        return !isARadiusCorner(item) || item.cornerInfo.size !== DEFAULT_CORNER_RADIUS;
    });

    const alertsFromSummary = removedWithoutDuplicatesWithoutCorners.reduce<AlertWithMetadata[]>((acc, curr) => {
        const { productName, imageUrl } = productInfoFromItem(curr);
        return [...acc, { label: `${productName}`, imageUrl }];
    }, emptyArray);
    return [...acc, ...alertsFromSummary];
};

export const summaryToAlertsReducer = (
    acc: string[],
    next: ProductMatchingSummary<EstimateItem, unknown>
): string[] => {
    /**The removed array of the summary without duplicate products */
    const removedWithoutDuplicatesObj = next.removed.reduce((acc, next) => {
        const { productId } = productInfoFromItem(next);
        if (!acc[productId]) {
            acc[productId] = next;
        }
        return acc;
    }, {} as Record<string, EstimateItem>);

    const alertsFromSummary = Object.values(removedWithoutDuplicatesObj).reduce<string[]>((acc, next) => {
        const { productName } = productInfoFromItem(next);
        return [...acc, `${productName}`];
    }, emptyArray);
    return [...acc, ...alertsFromSummary];
};

/**
 * Higher order function that return a reducer.
 * @returns a reducer. {@link DrawingShape} to {@link ProductMatchingSummary}
 */
export const createDrawingShapesToSummaryReducerUsingApplianceChecker = (newCatalog: ApplianceModel[]) => {
    return (
        acc: ProductMatchingSummary<DrawingAppliance, DrawingShape>,
        next: DrawingShape,
        index: number
    ): ProductMatchingSummary<DrawingAppliance, DrawingShape> => {
        const drawingAppliances = next.appliances;

        if (!drawingAppliances.length) {
            return acc;
        }

        const ctx: CheckerContext<ApplianceModel> = {
            newCatalog,
        };

        return drawingAppliances.reduce(
            (_acc, drawingAppliance) =>
                addToSummary({
                    summary: _acc,
                    item: drawingAppliance,
                    index,
                    checker: ApplianceChecker,
                    ctx,
                }),
            acc
        );
    };
};

/**
 * Higher order function that return a reducer.
 * @returns a reducer. {@link DrawingShape} to {@link ProductMatchingSummary}
 */
export const createDrawingShapesToSummaryReducer = (edgesNewCatalog: EdgeModel[]) => {
    return (
        acc: ProductMatchingSummary<EdgeModel, DrawingShape>,
        next: DrawingShape,
        index: number
    ): ProductMatchingSummary<EdgeModel, DrawingShape> => {
        const edge = next.edge;

        if (!edge) {
            return acc;
        }

        const ctx: CheckerContext<EdgeModel> = {
            newCatalog: edgesNewCatalog,
        };

        return addToSummary({
            summary: acc,
            item: edge,
            index,
            checker: EdgeChecker,
            ctx,
        });
    };
};

/**
 * To filter out undefined
 */
export const isSelectedItemWithPropertiesAndIsSelected = (
    selectedItemWithProperties: SelectedItemWithFullRecord | undefined
): selectedItemWithProperties is SelectedItemWithFullRecord =>
    selectedItemWithProperties !== undefined && selectedItemWithProperties.selectedItemProperties.isSelected;

/**
 * Return a function that takes a [string, SelectedItemProperties] and returns [CommonProductModel, SelectedItemProperties].
 * In order to accomplish this, a catalog is required. The arg products is the catalog
 * @param  products the current product catalog
 */
export const createSelectedItemToSelectedItemWithFullRecordMapper =
    <T extends OptionalItem>(products: T[]) =>
    ([key, selectedItemProperties]: [number, SelectedItemProperties]):
        | undefined
        | { selectedItemProperties: SelectedItemProperties; selectedItem: T } => {
        const productFullRecord = products.find((product) => product.productId === key);
        return productFullRecord && selectedItemProperties.quantity > 0
            ? {
                  // eslint-disable-next-line max-nested-callbacks
                  selectedItem: productFullRecord,
                  selectedItemProperties,
              }
            : undefined;
    };

export const addToSummary = <I, R extends { productId: number }, N>(args: {
    summary: ProductMatchingSummary<I, N>;
    item: I;
    index: number;
    checker: EquivalentProductChecker<I, R, N>;
    ctx: CheckerContext<R>;
}): ProductMatchingSummary<I, N> => {
    const { summary, item, index, checker, ctx } = args;

    const _existsInNewCatalog = checker.existsInNewCatalog({ item, ctx });
    const equivalent = checker.findEquivalent({ item, ctx });

    const builderCommonArguments: BuilderCommonArgs<I, R, N> = {
        ctx,
        index,
        item,
        oldValue: summary.newValueAfterReplacements,
    };

    const buildNewDesignItemOnRemoving =
        checker.buildNewDesignItemOnRemoving ?? ((args: BuilderCommonArgs<I, R, N>) => args.oldValue);

    if (_existsInNewCatalog) {
        // Keep
        const newValueAfterReplacements = checker.buildNewDesignItemOnKeepingItem(builderCommonArguments);
        return { ...summary, newValueAfterReplacements, kept: [...summary.kept, item] };
    } else if (!_existsInNewCatalog && equivalent) {
        // Replace with Equivalent
        const newValueAfterReplacements = checker.buildNewDesignItemOnReplacingWithEquivalent({
            ...builderCommonArguments,
            equivalent,
        });
        return {
            ...summary,
            newValueAfterReplacements,
            replacedWithEquivalent: [...summary.replacedWithEquivalent, { original: item, replacement: equivalent }],
        };
    } else if (checker.selectDefault && checker.buildNewValueUsingDefault) {
        // Removing or replacing with default
        const defaultItem = checker.selectDefault({ ctx });
        const newValueAfterReplacements = checker.buildNewValueUsingDefault({ ...builderCommonArguments, defaultItem });
        return {
            ...summary,
            newValueAfterReplacements,
            removed: [...summary.removed, item],
        };
    } else {
        const newValueAfterReplacements = buildNewDesignItemOnRemoving(builderCommonArguments);
        return {
            ...summary,
            newValueAfterReplacements,
            removed: [...summary.removed, item],
        };
    }
};

export const createSelectedItemToSummaryReducer =
    (newCatalog: (OptionalItem | PlumbingProduct)[]) =>
    (
        acc: ProductMatchingSummary<SelectedItemWithFullRecord, SelectedItemWithFullRecord>,
        next: SelectedItemWithFullRecord,
        index: number
    ): ProductMatchingSummary<SelectedItemWithFullRecord, SelectedItemWithFullRecord> => {
        const ctx: CheckerContext<OptionalItem | PlumbingProduct> = {
            newCatalog,
        };

        return addToSummary({
            summary: acc,
            item: next,
            index,
            checker: SelectedItemChecker,
            ctx,
        });
    };
